"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { staggerContainer, fadeInUp } from "@/lib/animations";

export default function PrivacyPolicy() {
	return (
		<div className="flex min-h-screen flex-col bg-background py-20 md:py-32">
			<div className="w-full px-6 md:px-12">
				<div className="mb-12">
					<Link
						href="/"
						className="group inline-flex items-center font-syne font-bold uppercase text-muted-foreground transition-colors hover:text-primary"
					>
						<ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
						Back to Home
					</Link>
				</div>

				<motion.div
					variants={staggerContainer}
					initial="hidden"
					animate="show"
					className="mx-auto max-w-4xl"
				>
					<motion.div variants={fadeInUp}>
						<h1 className="mb-12 font-syne text-5xl font-bold uppercase tracking-tighter md:text-7xl">
							Privacy <span className="text-primary">Policy</span>
						</h1>
					</motion.div>

					<motion.div variants={fadeInUp} className="space-y-12 font-inter text-lg leading-relaxed text-muted-foreground">
						<p className="text-sm font-bold uppercase tracking-widest text-primary">
							Last Updated: December 3, 2025
						</p>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								1. Introduction
							</h2>
							<p>
								TheHighrays (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is committed to protecting your privacy.
								This Privacy Policy explains how we collect, use, disclose, and safeguard your personal
								information when you visit our website thehighrays.com, use our services, subscribe to
								our newsletter, or interact with our brands including TheHighraysYT and Elec3D.
							</p>
							<p className="mt-4">
								By accessing our website, using our services, or providing us with your personal information,
								you consent to the practices described in this Privacy Policy. If you do not agree with our
								policies and practices, please do not use our services.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								2. Information We Collect
							</h2>
							<p className="mb-4">
								We collect information in several ways when you interact with our website and services:
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								2.1 Information You Provide Directly
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Contact Information:</strong> Name, email address,
									phone number, and physical address when you submit a contact form, place an order, or
									create an account.
								</li>
								<li>
									<strong className="text-foreground">Newsletter Subscriptions:</strong> Email address
									and any preferences you provide when subscribing to our newsletter.
								</li>
								<li>
									<strong className="text-foreground">Service Information:</strong> YouTube channel URLs,
									social media handles, project specifications, and other details necessary to deliver
									our services.
								</li>
								<li>
									<strong className="text-foreground">Payment Information:</strong> Billing address and
									payment method details (processed securely through third-party payment processors).
								</li>
								<li>
									<strong className="text-foreground">Communications:</strong> Content of messages,
									emails, and inquiries you send to us.
								</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								2.2 Information Collected Automatically
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Device Information:</strong> IP address, browser
									type and version, operating system, device type, and unique device identifiers.
								</li>
								<li>
									<strong className="text-foreground">Usage Data:</strong> Pages visited, time spent on
									pages, click patterns, referring URLs, and navigation paths through our website.
								</li>
								<li>
									<strong className="text-foreground">Location Data:</strong> General geographic location
									based on IP address (country and city level).
								</li>
								<li>
									<strong className="text-foreground">Cookies and Similar Technologies:</strong> Information
									collected through cookies, web beacons, and similar tracking technologies.
								</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								2.3 Information from Third Parties
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">YouTube and Social Platforms:</strong> Publicly
									available channel statistics and performance data when you engage our promotion services.
								</li>
								<li>
									<strong className="text-foreground">Analytics Providers:</strong> Aggregated data about
									website traffic and user behavior from services like Google Analytics.
								</li>
								<li>
									<strong className="text-foreground">Payment Processors:</strong> Transaction confirmation
									and billing information from our payment partners.
								</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								3. How We Use Your Information
							</h2>
							<p className="mb-4">
								We use the information we collect for the following purposes:
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								3.1 Service Delivery
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>Process and fulfill orders for YouTube promotion, Elec3D products, and other services</li>
								<li>Communicate with you about your orders, inquiries, and service updates</li>
								<li>Provide customer support and respond to your requests</li>
								<li>Deliver campaign reports and performance analytics</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								3.2 Marketing and Communications
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>Send newsletter updates, promotional offers, and company news (with your consent)</li>
								<li>Share educational content about YouTube growth, digital marketing, and technology</li>
								<li>Notify you about new services, products, and features</li>
								<li>Conduct surveys and gather feedback to improve our offerings</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								3.3 Website Improvement and Analytics
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>Analyze usage patterns to improve website functionality and user experience</li>
								<li>Personalize content and recommendations based on your interests</li>
								<li>Monitor and optimize website performance and security</li>
								<li>Conduct research and analysis to develop new features and services</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								3.4 Legal and Security Purposes
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>Comply with applicable laws, regulations, and legal processes</li>
								<li>Protect against fraud, unauthorized transactions, and security threats</li>
								<li>Enforce our Terms and Conditions and other agreements</li>
								<li>Protect the rights, property, and safety of TheHighrays and our users</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								4. Newsletter and Email Communications
							</h2>
							<p>
								When you subscribe to our newsletter or provide your email through our contact form,
								we collect and store your email address in our database hosted on Supabase. Your email
								subscription data is used exclusively for:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>Sending periodic newsletters with updates, tips, and industry insights</li>
								<li>Sharing promotional offers and new service announcements</li>
								<li>Providing educational content about YouTube growth and digital marketing</li>
								<li>Responding to inquiries submitted through contact forms</li>
							</ul>
							<p className="mt-4">
								<strong className="text-foreground">Opt-Out:</strong> You can unsubscribe from marketing
								emails at any time by clicking the &quot;unsubscribe&quot; link at the bottom of any email or by
								contacting us at info@thehighrays.com. Please note that you may still receive transactional
								emails related to active orders or services.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								5. Cookies and Tracking Technologies
							</h2>
							<p className="mb-4">
								We use cookies and similar tracking technologies to enhance your experience on our website:
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								5.1 Types of Cookies We Use
							</h3>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Essential Cookies:</strong> Necessary for website
									functionality, including session management and security features.
								</li>
								<li>
									<strong className="text-foreground">Analytics Cookies:</strong> Help us understand how
									visitors interact with our website through services like Google Analytics.
								</li>
								<li>
									<strong className="text-foreground">Preference Cookies:</strong> Remember your settings
									and preferences, such as language and theme (light/dark mode).
								</li>
								<li>
									<strong className="text-foreground">Marketing Cookies:</strong> Used to track visitors
									across websites to display relevant advertisements (when applicable).
								</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								5.2 Managing Cookies
							</h3>
							<p>
								Most web browsers allow you to control cookies through their settings. You can set your
								browser to refuse all cookies or to indicate when a cookie is being sent. However,
								disabling cookies may affect your ability to use certain features of our website.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								6. Data Sharing and Disclosure
							</h2>
							<p className="mb-4">
								We do not sell, rent, or trade your personal information. We may share your information
								in the following circumstances:
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								6.1 Service Providers
							</h3>
							<p>
								We share information with trusted third-party service providers who assist us in operating
								our website and delivering our services, including:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Supabase:</strong> Database hosting and backend
									services for storing contact form submissions and newsletter subscriptions.
								</li>
								<li>
									<strong className="text-foreground">Payment Processors:</strong> Secure processing of
									payment transactions (we do not store full payment card details).
								</li>
								<li>
									<strong className="text-foreground">Email Services:</strong> Delivery of transactional
									and marketing emails.
								</li>
								<li>
									<strong className="text-foreground">Analytics Providers:</strong> Website analytics
									and performance monitoring.
								</li>
								<li>
									<strong className="text-foreground">Cloud Hosting:</strong> Website and application
									hosting infrastructure.
								</li>
							</ul>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								6.2 Legal Requirements
							</h3>
							<p>
								We may disclose your information when required by law, court order, or government
								regulation, or when we believe disclosure is necessary to protect our rights, your
								safety, or the safety of others.
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								6.3 Business Transfers
							</h3>
							<p>
								In the event of a merger, acquisition, or sale of assets, your information may be
								transferred as part of the transaction. We will notify you of any change in ownership
								or control of your personal information.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								7. Data Security
							</h2>
							<p>
								We implement industry-standard security measures to protect your personal information:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Encryption:</strong> All data transmitted to and
									from our website is encrypted using SSL/TLS technology.
								</li>
								<li>
									<strong className="text-foreground">Secure Storage:</strong> Personal data is stored
									on secure servers with access controls and encryption at rest.
								</li>
								<li>
									<strong className="text-foreground">Access Controls:</strong> Only authorized personnel
									have access to personal information, on a need-to-know basis.
								</li>
								<li>
									<strong className="text-foreground">Regular Audits:</strong> We conduct regular security
									assessments and updates to our systems.
								</li>
								<li>
									<strong className="text-foreground">Payment Security:</strong> Payment information is
									processed through PCI-DSS compliant payment processors. We do not store full credit
									card numbers on our servers.
								</li>
							</ul>
							<p className="mt-4">
								While we strive to protect your information, no method of transmission over the internet
								or electronic storage is 100% secure. We cannot guarantee absolute security.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								8. Data Retention
							</h2>
							<p>
								We retain your personal information for as long as necessary to fulfill the purposes
								outlined in this Privacy Policy, unless a longer retention period is required or
								permitted by law:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Account Information:</strong> Retained while your
									account is active and for a reasonable period thereafter.
								</li>
								<li>
									<strong className="text-foreground">Transaction Records:</strong> Retained for 7 years
									for tax and legal compliance purposes.
								</li>
								<li>
									<strong className="text-foreground">Newsletter Subscriptions:</strong> Retained until
									you unsubscribe, after which your email may be retained in a suppression list to prevent
									future mailings.
								</li>
								<li>
									<strong className="text-foreground">Contact Form Submissions:</strong> Retained for 3
									years or until the matter is resolved, whichever is later.
								</li>
								<li>
									<strong className="text-foreground">Analytics Data:</strong> Aggregated and anonymized
									data may be retained indefinitely.
								</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								9. Your Rights and Choices
							</h2>
							<p className="mb-4">
								Depending on your location, you may have certain rights regarding your personal information:
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								9.1 Access and Portability
							</h3>
							<p>
								You have the right to request a copy of the personal information we hold about you
								in a structured, commonly used, and machine-readable format.
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								9.2 Correction
							</h3>
							<p>
								You have the right to request correction of any inaccurate or incomplete personal
								information we hold about you.
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								9.3 Deletion
							</h3>
							<p>
								You have the right to request deletion of your personal information, subject to
								certain exceptions (such as legal compliance requirements or ongoing contractual
								obligations).
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								9.4 Opt-Out of Marketing
							</h3>
							<p>
								You can opt out of receiving marketing communications at any time by clicking the
								unsubscribe link in any email or contacting us directly.
							</p>

							<h3 className="mt-6 mb-3 font-syne text-xl font-bold text-foreground">
								9.5 Exercising Your Rights
							</h3>
							<p>
								To exercise any of these rights, please contact us at info@thehighrays.com with your
								request. We will respond within 30 days and may require identity verification before
								processing your request.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								10. International Data Transfers
							</h2>
							<p>
								TheHighrays is based in India, and our services are provided globally. Your information
								may be transferred to, stored, and processed in countries other than your country of
								residence, including India and countries where our service providers operate.
							</p>
							<p className="mt-4">
								By using our services, you consent to the transfer of your information to countries
								that may have different data protection laws than your country of residence. We take
								appropriate measures to ensure your data is protected in accordance with this Privacy
								Policy regardless of where it is processed.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								11. Third-Party Links
							</h2>
							<p>
								Our website may contain links to third-party websites, including YouTube, social media
								platforms, and partner sites. We are not responsible for the privacy practices or
								content of these third-party sites. We encourage you to review the privacy policies
								of any third-party sites you visit.
							</p>
							<p className="mt-4">
								Links to third-party sites do not constitute endorsement or approval of their content,
								products, services, or privacy practices.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								12. Children&apos;s Privacy
							</h2>
							<p>
								Our website and services are not directed to children under the age of 13 (or 16 in
								some jurisdictions). We do not knowingly collect personal information from children.
								If you are a parent or guardian and believe your child has provided us with personal
								information, please contact us immediately at info@thehighrays.com.
							</p>
							<p className="mt-4">
								If we become aware that we have collected personal information from a child without
								verified parental consent, we will take steps to delete that information promptly.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								13. Do Not Track Signals
							</h2>
							<p>
								Some web browsers may transmit &quot;Do Not Track&quot; signals to websites. Due to the lack
								of a common standard for interpreting these signals, our website does not currently
								respond to Do Not Track signals. However, you can manage your cookie preferences
								through your browser settings.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								14. California Privacy Rights (CCPA)
							</h2>
							<p className="mb-4">
								If you are a California resident, you have additional rights under the California
								Consumer Privacy Act (CCPA):
							</p>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Right to Know:</strong> You can request information
									about the categories and specific pieces of personal information we have collected.
								</li>
								<li>
									<strong className="text-foreground">Right to Delete:</strong> You can request deletion
									of personal information we have collected.
								</li>
								<li>
									<strong className="text-foreground">Right to Non-Discrimination:</strong> We will not
									discriminate against you for exercising your CCPA rights.
								</li>
								<li>
									<strong className="text-foreground">No Sale of Personal Information:</strong> We do not
									sell personal information as defined by the CCPA.
								</li>
							</ul>
							<p className="mt-4">
								To exercise your CCPA rights, contact us at info@thehighrays.com.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								15. European Privacy Rights (GDPR)
							</h2>
							<p className="mb-4">
								If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland,
								you have rights under the General Data Protection Regulation (GDPR):
							</p>
							<ul className="list-disc space-y-2 pl-6 marker:text-primary">
								<li>
									<strong className="text-foreground">Legal Basis:</strong> We process your data based
									on consent, contractual necessity, legitimate interests, or legal obligations.
								</li>
								<li>
									<strong className="text-foreground">Data Subject Rights:</strong> You have the right
									to access, rectify, erase, restrict processing, data portability, and object to processing.
								</li>
								<li>
									<strong className="text-foreground">Withdrawal of Consent:</strong> You can withdraw
									consent at any time without affecting the lawfulness of prior processing.
								</li>
								<li>
									<strong className="text-foreground">Right to Lodge Complaint:</strong> You have the
									right to lodge a complaint with a supervisory authority in your jurisdiction.
								</li>
							</ul>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								16. Changes to This Privacy Policy
							</h2>
							<p>
								We may update this Privacy Policy from time to time to reflect changes in our practices,
								technologies, legal requirements, or other factors. When we make material changes, we
								will notify you by:
							</p>
							<ul className="mt-4 list-disc space-y-2 pl-6 marker:text-primary">
								<li>Posting the updated policy on this page with a new &quot;Last Updated&quot; date</li>
								<li>Sending an email notification to registered users (for significant changes)</li>
								<li>Displaying a prominent notice on our website</li>
							</ul>
							<p className="mt-4">
								We encourage you to review this Privacy Policy periodically to stay informed about
								how we protect your information.
							</p>
						</section>

						<section>
							<h2 className="mb-4 font-syne text-2xl font-bold uppercase text-foreground">
								17. Contact Information
							</h2>
							<p className="mb-4">
								If you have any questions, concerns, or requests regarding this Privacy Policy or our
								data practices, please contact us:
							</p>
							<div className="border-l-2 border-primary pl-6">
								<p className="font-bold text-foreground">TheHighrays - Data Protection</p>
								<p className="mt-2">
									<strong className="text-foreground">Email:</strong>{" "}
									info@thehighrays.com
								</p>
								<p>
									<strong className="text-foreground">Phone:</strong> +91 8273262260
								</p>
								<p>
									<strong className="text-foreground">Address:</strong> New Delhi, Delhi, India
								</p>
								<p className="mt-4 text-sm">
									Business Hours: Monday - Friday, 10:00 AM - 6:00 PM IST
								</p>
							</div>
							<p className="mt-6">
								For privacy-related inquiries, we aim to respond within 48 hours during business days.
								For formal data subject requests, we will respond within 30 days as required by applicable law.
							</p>
						</section>
					</motion.div>
				</motion.div>
			</div>
		</div>
	);
}
